import React, { useState, useRef, useEffect } from 'react';
import LoadingModal from './LoadingModal';
import { generateInitialsAvatar, generateDefaultCover, isEmptyOrInvalidImage } from '../utils/avatarGenerator';

interface PhotoManagerProps {
  currentProfilePhoto?: string;
  currentCoverPhoto?: string;
  onProfilePhotoUpdate: (photoUrl: string) => void;
  onCoverPhotoUpdate: (photoUrl: string) => void;
  professionalId: number;
}

const ProfessionalPhotoManager: React.FC<PhotoManagerProps> = ({
  currentProfilePhoto,
  currentCoverPhoto,
  onProfilePhotoUpdate,
  onCoverPhotoUpdate,
  professionalId
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [defaultProfilePhoto, setDefaultProfilePhoto] = useState<string>('');
  const [defaultCoverPhoto, setDefaultCoverPhoto] = useState<string>('');
 
  const profileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Obtener la URL base del backend desde las variables de entorno
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Generar avatares por defecto cuando el componente se monta
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const { nombre = 'Usuario', apellido = 'Nuevo' } = userData;
      
      // Generar avatar con iniciales para perfil
      const profileAvatar = generateInitialsAvatar(nombre, apellido, 400);
      setDefaultProfilePhoto(profileAvatar);
      
      // Generar portada por defecto
      const coverImage = generateDefaultCover(nombre, apellido);
      setDefaultCoverPhoto(coverImage);
    } catch (error) {
      console.error('Error generando avatares por defecto:', error);
    }
  }, []);

  // Función para actualizar localStorage y disparar evento
  const updateLocalStorageAndNotify = (userData: any) => {
    localStorage.setItem('user', JSON.stringify(userData));
    // Disparar evento personalizado para notificar cambios
    window.dispatchEvent(new CustomEvent('userDataUpdated'));
  };

  const handleFileUpload = async (file: File, type: 'profile' | 'cover') => {
    if (!file) return;
    
    // Validaciones
    if (!file.type.startsWith('image/')) {
      setUploadMessage({ type: 'error', text: 'Solo se permiten imágenes' });
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setUploadMessage({ type: 'error', text: 'La imagen debe ser menor a 5MB' });
      return;
    }

    setIsUploading(true);
    setUploadMessage({ type: '', text: '' });

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;
       
        try {
          // El endpoint pasa a pedir token y a verificar que sea tu perfil.
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_BASE_URL}/api/professionals/${professionalId}/photos`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              photoType: type,
              imageData: base64Data,
              filename: file.name
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Error al subir la imagen');
          }

          const result = await response.json();
          
          console.log('Respuesta del servidor:', result);
         
          // Construir URL completa usando la URL base del backend
          const fullPhotoUrl = result.photoUrl.startsWith('http') 
            ? result.photoUrl 
            : `${API_BASE_URL}${result.photoUrl}`;
          
          // Actualizar el componente padre
          if (type === 'profile') {
            onProfilePhotoUpdate(fullPhotoUrl);
          } else {
            onCoverPhotoUpdate(fullPhotoUrl);
          }

          // Actualizar localStorage con los datos devueltos por el servidor
          try {
            const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
            const updatedUserData = {
              ...currentUserData,
              [type === 'profile' ? 'picture' : 'cover_photo']: fullPhotoUrl
            };
            
            updateLocalStorageAndNotify(updatedUserData);
          } catch (localStorageError) {
            console.warn('Error actualizando localStorage:', localStorageError);
          }

          setUploadMessage({
            type: 'success',
            text: `Foto ${type === 'profile' ? 'de perfil' : 'de portada'} actualizada exitosamente`
          });

          // Limpiar input
          if (type === 'profile' && profileInputRef.current) {
            profileInputRef.current.value = '';
          } else if (type === 'cover' && coverInputRef.current) {
            coverInputRef.current.value = '';
          }

        } catch (error) {
          console.error('Error uploading image:', error);
          setUploadMessage({ 
            type: 'error', 
            text: error instanceof Error ? error.message : 'Error al subir la imagen' 
          });
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        setUploadMessage({ type: 'error', text: 'Error procesando la imagen' });
        setIsUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      setUploadMessage({ type: 'error', text: 'Error procesando la imagen' });
      setIsUploading(false);
    }
  };

  const handleProfilePhotoClick = () => {
    profileInputRef.current?.click();
  };

  const handleCoverPhotoClick = () => {
    coverInputRef.current?.click();
  };

  const removePhoto = async (type: 'profile' | 'cover') => {
    setIsUploading(true);
    setUploadMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/professionals/${professionalId}/photos/${type}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar la foto');
      }

      const result = await response.json();

      // Actualizar el componente padre
      if (type === 'profile') {
        onProfilePhotoUpdate('');
      } else {
        onCoverPhotoUpdate('');
      }

      // Actualizar localStorage
      try {
        const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUserData = {
          ...currentUserData,
          [type === 'profile' ? 'picture' : 'cover_photo']: null
        };
        
        updateLocalStorageAndNotify(updatedUserData);
      } catch (localStorageError) {
        console.warn('Error actualizando localStorage:', localStorageError);
      }

      setUploadMessage({
        type: 'success',
        text: `Foto ${type === 'profile' ? 'de perfil' : 'de portada'} eliminada exitosamente`
      });

    } catch (error) {
      console.error('Error removing photo:', error);
      setUploadMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Error al eliminar la foto' 
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Determinar qué foto mostrar (con fallback a avatares por defecto)
  const displayProfilePhoto = isEmptyOrInvalidImage(currentProfilePhoto) 
    ? defaultProfilePhoto 
    : currentProfilePhoto;
    
  const displayCoverPhoto = isEmptyOrInvalidImage(currentCoverPhoto)
    ? defaultCoverPhoto
    : currentCoverPhoto;

  // Determinar si mostrar el botón de eliminar
  const hasCustomProfilePhoto = !isEmptyOrInvalidImage(currentProfilePhoto);
  const hasCustomCoverPhoto = !isEmptyOrInvalidImage(currentCoverPhoto);

  return (
    <>
      {/* Modal de loading */}
      <LoadingModal 
        isOpen={isUploading} 
        message="Subiendo imagen..."
      />

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Gestión de Fotos</h2>
       
        {uploadMessage.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            uploadMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {uploadMessage.text}
          </div>
        )}

        <div className="space-y-8">
          {/* Foto de portada */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Foto de portada</h3>
            <div className="relative">
              <div className="h-48 w-full rounded-xl overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 relative">
                {displayCoverPhoto ? (
                  <img
                    src={displayCoverPhoto}
                    alt="Portada"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.warn('Error cargando imagen de portada:', displayCoverPhoto);
                      e.currentTarget.src = defaultCoverPhoto;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <div className="text-center">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm">Sin foto de portada</p>
                    </div>
                  </div>
                )}
               
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <div className="space-x-3">
                    <button
                      onClick={handleCoverPhotoClick}
                      disabled={isUploading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {isUploading ? 'Subiendo...' : (hasCustomCoverPhoto ? 'Cambiar' : 'Subir')} portada
                    </button>
                    {hasCustomCoverPhoto && (
                      <button
                        onClick={() => removePhoto('cover')}
                        disabled={isUploading}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Foto de perfil */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Foto de perfil</h3>
            <div className="flex items-start space-x-6">
              <div className="relative flex-shrink-0">
                <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-green-500 relative">
                  {displayProfilePhoto ? (
                    <img
                      src={displayProfilePhoto}
                      alt="Perfil"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.warn('Error cargando imagen de perfil:', displayProfilePhoto);
                        e.currentTarget.src = defaultProfilePhoto;
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl">
                      <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                 
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <button
                      onClick={handleProfilePhotoClick}
                      disabled={isUploading}
                      className="bg-white text-gray-800 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      {isUploading ? 'Subiendo...' : (hasCustomProfilePhoto ? 'Cambiar' : 'Subir')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 mb-2">Consejos para una buena foto de perfil:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Usa una foto clara y profesional</li>
                  <li>• Asegúrate de que tu rostro sea visible</li>
                  <li>• Evita selfies o fotos demasiado casuales</li>
                  <li>• La imagen debe ser cuadrada (1:1) para mejores resultados</li>
                  <li>• Formato recomendado: JPG o PNG, máximo 5MB</li>
                </ul>
                
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={handleProfilePhotoClick}
                    disabled={isUploading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isUploading ? 'Subiendo...' : hasCustomProfilePhoto ? 'Cambiar foto' : 'Subir foto'}
                  </button>
                 
                  {hasCustomProfilePhoto && (
                    <button
                      onClick={() => removePhoto('profile')}
                      disabled={isUploading}
                      className="border border-red-300 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      Eliminar foto
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Inputs ocultos para selección de archivos */}
          <input
            ref={profileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file, 'profile');
            }}
            className="hidden"
          />
          
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file, 'cover');
            }}
            className="hidden"
          />

          {/* Información de debug (solo en desarrollo) */}
          {process.env.REACT_APP_DEBUG_MODE === 'true' && (
            <div className="mt-4 p-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Debug Info:</strong> API Base URL: {API_BASE_URL}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <strong>Profile Photo:</strong> {hasCustomProfilePhoto ? '✅ Custom' : '🎨 Default'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Cover Photo:</strong> {hasCustomCoverPhoto ? '✅ Custom' : '🎨 Default'}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfessionalPhotoManager;